import React from "react";
import { X, Bell, Ticket, AlertTriangle } from "lucide-react";
import {
  notifications as notificationsApi,
  type Notification,
} from "../../api/client";
import { useQuery } from "../../hooks/useApi";
import { formatDistanceToNow } from "date-fns";

interface NotificationsFlyoutProps {
  isOpen: boolean;
  onClose: () => void;
}

function formatTimeAgo(dateStr: string): string {
  try {
    return formatDistanceToNow(new Date(dateStr), { addSuffix: true });
  } catch {
    return dateStr;
  }
}

function getCategoryForType(type: string): string {
  switch (type) {
    case "ticket":
      return "TICKETS";
    case "alert":
      return "SYSTEM";
    default:
      return "OTHER";
  }
}

export function NotificationsFlyout({
  isOpen,
  onClose,
}: NotificationsFlyoutProps) {
  const {
    data: notificationsData,
    isLoading,
    refetch,
  } = useQuery(() => notificationsApi.list());

  const notifications = (notificationsData ?? []).map((n) => ({
    id: n.notif_id,
    type: n.type,
    description: n.summary,
    timeAgo: formatTimeAgo(n.created_at),
    read: n.read,
    category: getCategoryForType(n.type),
  }));

  const unreadCount = notifications.filter((n) => !n.read).length;

  const handleMarkAllRead = async () => {
    const unread = notificationsData?.filter((n) => !n.read) ?? [];
    try {
      await Promise.all(
        unread.map((n) => notificationsApi.markRead(n.notif_id)),
      );
      refetch();
    } catch {
      // Silently handle errors
    }
  };

  const getIconForType = (type: string) => {
    switch (type) {
      case "ticket":
        return <Ticket className="w-5 h-5 text-duvi-accent" />;
      case "alert":
        return <AlertTriangle className="w-5 h-5 text-duvi-warning" />;
      default:
        return <Bell className="w-5 h-5 text-duvi-text-secondary" />;
    }
  };

  const groupedNotifications = notifications.reduce(
    (acc, notification) => {
      if (!acc[notification.category]) {
        acc[notification.category] = [];
      }
      acc[notification.category].push(notification);
      return acc;
    },
    {} as Record<string, typeof notifications>,
  );

  const EmptyState = () => (
    <div className="flex flex-col items-center justify-center py-16 px-6">
      <Bell
        className="w-12 h-12 text-duvi-text-secondary mb-6"
        strokeWidth={1}
      />
      <h2
        className="text-duvi-text-primary mb-2"
        style={{
          fontSize: "var(--text-lg)",
          fontWeight: "var(--font-weight-semibold)",
        }}
      >
        All caught up
      </h2>
      <p
        className="text-duvi-text-secondary text-center"
        style={{ fontSize: "var(--text-sm)" }}
      >
        No new notifications.
      </p>
    </div>
  );

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className={`fixed top-0 right-0 h-full bg-duvi-elevated border-l border-duvi-border shadow-2xl z-50 flex flex-col transition-transform duration-300 ease-in-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
        style={{ width: "360px" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-4 border-b border-duvi-border">
          <div className="flex items-center gap-2">
            <h2
              className="text-duvi-text-primary"
              style={{
                fontSize: "var(--text-lg)",
                fontWeight: "var(--font-weight-bold)",
              }}
            >
              Notifications
            </h2>
            {unreadCount > 0 && (
              <span
                className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-duvi-accent text-white rounded-full"
                style={{
                  fontSize: "var(--text-2xs)",
                  fontWeight: "var(--font-weight-bold)",
                }}
              >
                {unreadCount}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-duvi-accent hover:underline"
                style={{ fontSize: "var(--text-xs)" }}
              >
                Mark all read
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1 hover:bg-duvi-surface rounded transition-colors text-duvi-text-secondary"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div
              className="text-duvi-text-secondary"
              style={{ fontSize: "var(--text-sm)" }}
            >
              Loading...
            </div>
          </div>
        ) : notifications.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            <div className="flex-1 overflow-y-auto">
              {Object.entries(groupedNotifications).map(([category, items]) => (
                <div key={category}>
                  {/* Section Label */}
                  <div
                    className="px-4 py-2 bg-duvi-surface text-duvi-text-secondary"
                    style={{
                      fontSize: "var(--text-xs)",
                      fontWeight: "var(--font-weight-medium)",
                      letterSpacing: "0.05em",
                    }}
                  >
                    {category}
                  </div>

                  {/* Notification Items */}
                  {items.map((notification) => (
                    <div
                      key={notification.id}
                      className={`relative px-4 py-3 border-b border-duvi-border hover:bg-duvi-bg transition-colors cursor-pointer ${
                        !notification.read ? "bg-duvi-bg/30" : ""
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        {/* Icon */}
                        <div className="flex-shrink-0 mt-0.5">
                          {getIconForType(notification.type)}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <p
                            className="text-duvi-text-primary"
                            style={{ fontSize: "var(--text-sm)" }}
                          >
                            {notification.description}
                          </p>
                          <p
                            className="text-duvi-text-secondary mt-1"
                            style={{ fontSize: "var(--text-2xs)" }}
                          >
                            {notification.timeAgo}
                          </p>
                        </div>

                        {/* Unread Dot */}
                        {!notification.read && (
                          <div className="flex-shrink-0 mt-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-duvi-accent" />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="px-4 py-4 border-t border-duvi-border text-center">
              <button
                className="text-duvi-accent hover:underline"
                style={{ fontSize: "var(--text-xs)" }}
              >
                View all notifications
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
